import { useState } from "react";
import axios from "axios";
import { format } from "date-fns";
import { ro } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import {
  CalendarIcon,
  Clock,
  Users,
  MapPin,
  User,
  Phone,
  Send,
  CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HOURS_OPTIONS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES_OPTIONS = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

const PERSOANE_OPTIONS = ["1", "2", "3", "4", "5", "6", "7", "8", "10", "12", "15", "17", "19"];

export default function Booking() {
  const [form, setForm] = useState({
    nume: "",
    telefon: "",
    plecare: "Mangalia",
    destinatie: "",
    ora_h: "",
    ora_m: "",
    persoane: "",
    mesaj: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [waUrl, setWaUrl] = useState("");

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error("Te rugăm să alegi data călătoriei.");
      return;
    }
    if (!form.ora_h || !form.ora_m) {
      toast.error("Te rugăm să alegi ora și minutul.");
      return;
    }
    if (!form.persoane) {
      toast.error("Te rugăm să selectezi numărul de persoane.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        nume: form.nume.trim(),
        telefon: form.telefon.trim(),
        plecare: form.plecare.trim(),
        destinatie: form.destinatie.trim(),
        data: format(date, "yyyy-MM-dd"),
        ora: `${form.ora_h}:${form.ora_m}`,
        persoane: parseInt(form.persoane, 10),
        mesaj: form.mesaj.trim(),
      };
      const res = await axios.post(`${API}/bookings`, payload);
      const bookingId = res?.data?.id || "";

      // Google Ads conversion tracking
      if (typeof window !== "undefined" && typeof window.gtag === "function") {
        window.gtag("event", "conversion", {
          send_to: "AW-10958824237/VdXlCJOfl60cEK3Gyeko",
          transaction_id: bookingId,
        });
      }

      // Construiește mesaj WhatsApp
      const waNumber = "40754539886";
      const waText =
        `Bună ziua! Doresc să rezerv o cursă GlobalTransport:\n\n` +
        `• Nume: ${payload.nume}\n` +
        `• Telefon: ${payload.telefon}\n` +
        `• Plecare: ${payload.plecare}\n` +
        `• Destinație: ${payload.destinatie}\n` +
        `• Data: ${payload.data}\n` +
        `• Ora: ${payload.ora}\n` +
        `• Persoane: ${payload.persoane}\n` +
        (payload.mesaj ? `• Mesaj: ${payload.mesaj}\n` : "") +
        `\nMulțumesc!`;
      const url = `https://wa.me/${waNumber}?text=${encodeURIComponent(waText)}`;
      setWaUrl(url);

      toast.success("Rezervare trimisă! Te redirecționăm către WhatsApp...");
      setSuccess(true);
      setForm({
        nume: "",
        telefon: "",
        plecare: "Mangalia",
        destinatie: "",
        ora_h: "",
        ora_m: "",
        persoane: "",
        mesaj: "",
      });
      setDate(null);

      // Redirect direct la WhatsApp (cu un mic delay pentru a permite afișarea toast-ului)
      setTimeout(() => {
        window.location.href = url;
      }, 800);
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Eroare la trimiterea rezervării. Te rugăm să încerci din nou.";
      toast.error(typeof msg === "string" ? msg : "Eroare la trimiterea rezervării.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="rezervare"
      data-testid="booking-section"
      className="py-20 lg:py-28 bg-slate-900 text-white relative overflow-hidden"
    >
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-amber-500/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-32 w-96 h-96 rounded-full bg-amber-500/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="text-amber-400 font-semibold text-sm uppercase tracking-widest">
            Rezervare
          </span>
          <h2
            data-testid="booking-title"
            className="mt-3 text-3xl lg:text-4xl font-bold"
          >
            Rezervă-ți cursa în mai puțin de un minut.
          </h2>
          <p className="mt-4 text-slate-300 max-w-xl mx-auto">
            Completează formularul și apasă <strong className="text-amber-400">Trimite rezervarea</strong> —
            se deschide automat WhatsApp cu detaliile cursei pentru confirmare instantă.
          </p>
        </div>

        {success ? (
          <div
            data-testid="booking-success"
            className="bg-slate-900 rounded-2xl p-10 text-center text-white"
          >
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto" />
            <h3 className="mt-4 text-2xl font-bold">
              Rezervare înregistrată!
            </h3>
            <p className="mt-2 text-slate-300 max-w-md mx-auto">
              Te redirecționăm automat către WhatsApp cu detaliile cursei
              completate. Dacă nu se deschide singur, apasă butonul de mai jos.
              Apoi apasă <strong>Trimite</strong> în WhatsApp pentru a finaliza.
            </p>
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-whatsapp-btn"
              className="mt-6 inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1faa54] text-white font-semibold rounded-full px-8 h-12 transition-colors"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current">
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 0 1-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 0 1 8.413 3.488 11.824 11.824 0 0 1 3.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 0 1-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 0 0 1.599 5.391l-.999 3.648 3.889-1.738zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.297-.495.099-.198.05-.371-.025-.521-.074-.149-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.521.074-.794.371s-1.041 1.016-1.041 2.479 1.066 2.876 1.215 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.414z"/>
              </svg>
              Deschide WhatsApp
            </a>
            <div className="mt-6">
              <Button
                data-testid="booking-new-btn"
                onClick={() => {
                  setSuccess(false);
                  setWaUrl("");
                }}
                variant="outline"
                className="rounded-full"
              >
                Trimite o nouă rezervare
              </Button>
            </div>
          </div>
        ) : (
          <form
            data-testid="booking-form"
            onSubmit={handleSubmit}
            className="bg-slate-900 rounded-2xl p-6 sm:p-10 text-white shadow-2xl"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field
                icon={User}
                id="nume"
                label="Nume complet *"
                value={form.nume}
                onChange={update("nume")}
                placeholder="Ion Popescu"
                required
                testid="booking-input-nume"
              />
              <Field
                icon={Phone}
                id="telefon"
                label="Telefon *"
                value={form.telefon}
                onChange={update("telefon")}
                placeholder="07XX XXX XXX"
                required
                testid="booking-input-telefon"
              />
              <div className="sm:col-span-1">
                <Label htmlFor="persoane" className="text-slate-200 font-medium">
                  Număr persoane *
                </Label>
                <div className="relative mt-1.5">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10" />
                  <Select
                    value={form.persoane}
                    onValueChange={(v) => setForm((f) => ({ ...f, persoane: v }))}
                  >
                    <SelectTrigger
                      data-testid="booking-select-persoane"
                      className="pl-9 h-11 border-slate-700"
                    >
                      <SelectValue placeholder="Alege..." />
                    </SelectTrigger>
                    <SelectContent>
                      {PERSOANE_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>
                          {p} {p === "1" ? "persoană" : "persoane"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Field
                icon={MapPin}
                id="plecare"
                label="Plecare *"
                value={form.plecare}
                onChange={update("plecare")}
                placeholder="Mangalia"
                required
                testid="booking-input-plecare"
              />
              <Field
                icon={MapPin}
                id="destinatie"
                label="Destinație *"
                value={form.destinatie}
                onChange={update("destinatie")}
                placeholder="Aeroport Otopeni"
                required
                testid="booking-input-destinatie"
              />

              <div>
                <Label className="text-slate-200 font-medium">
                  Data călătoriei *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      data-testid="booking-date-trigger"
                      className="w-full mt-1.5 h-11 justify-start text-left font-normal border-slate-700 hover:bg-slate-950"
                    >
                      <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
                      {date ? (
                        format(date, "d MMMM yyyy", { locale: ro })
                      ) : (
                        <span className="text-slate-400">Alege data</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) =>
                        d < new Date(new Date().setHours(0, 0, 0, 0))
                      }
                      locale={ro}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label htmlFor="ora_h" className="text-slate-200 font-medium">
                  Ora plecării *
                </Label>
                <div className="mt-1.5 grid grid-cols-2 gap-2">
                  <div className="relative">
                    <Clock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 z-10 pointer-events-none" />
                    <Select
                      value={form.ora_h}
                      onValueChange={(v) => setForm((f) => ({ ...f, ora_h: v }))}
                    >
                      <SelectTrigger
                        data-testid="booking-select-ora-h"
                        className="pl-9 h-11 border-slate-700"
                      >
                        <SelectValue placeholder="Oră" />
                      </SelectTrigger>
                      <SelectContent className="max-h-64">
                        {HOURS_OPTIONS.map((h) => (
                          <SelectItem key={h} value={h}>
                            {h}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Select
                    value={form.ora_m}
                    onValueChange={(v) => setForm((f) => ({ ...f, ora_m: v }))}
                  >
                    <SelectTrigger
                      data-testid="booking-select-ora-m"
                      className="h-11 border-slate-700"
                    >
                      <SelectValue placeholder="Minut" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64">
                      {MINUTES_OPTIONS.map((m) => (
                        <SelectItem key={m} value={m}>
                          {m}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <Label htmlFor="mesaj" className="text-slate-200 font-medium">
                Detalii suplimentare (opțional)
              </Label>
              <Textarea
                id="mesaj"
                data-testid="booking-input-mesaj"
                value={form.mesaj}
                onChange={update("mesaj")}
                placeholder="Bagaje voluminoase, scaun copil, opriri intermediare..."
                className="mt-1.5 border-slate-700 min-h-[100px]"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="booking-submit-btn"
              className="mt-7 w-full sm:w-auto bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-full px-8 h-12 amber-glow"
            >
              {submitting ? (
                "Se trimite..."
              ) : (
                <>
                  Trimite rezervarea
                  <Send className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
            <p className="mt-3 text-xs text-slate-400">
              Datele tale sunt folosite exclusiv pentru confirmarea rezervării.
            </p>
          </form>
        )}
      </div>
    </section>
  );
}

function Field({ icon: Icon, id, label, type = "text", testid, ...rest }) {
  return (
    <div>
      <Label htmlFor={id} className="text-slate-200 font-medium">
        {label}
      </Label>
      <div className="relative mt-1.5">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          id={id}
          type={type}
          data-testid={testid}
          className="pl-9 h-11 border-slate-700"
          {...rest}
        />
      </div>
    </div>
  );
}
